import { useEffect, useState } from "react";
import API_BASE from "../config";

/**
 * Feature flag for the public homepage (`/`, `/news/:id`, `/programs/:slug`).
 *
 * When ON (default): the school's marketing landing page + linked
 * news / program pages are accessible without authentication, exactly
 * like a normal public school website.
 *
 * When OFF: every public route silently redirects to `/login` so the
 * site behaves like a members-only system. The login form is the
 * front door; nothing about the school is visible to the open
 * internet. Useful for installations that don't want a public face
 * (e.g. internal-use deployments, soft-launches before the homepage
 * is ready, or tightly-controlled pilots).
 *
 * Persistence: stored as `homepage.enabled` on `/api/site-content`,
 * same shared key/value table the Payroll module flag uses. Same
 * hot-cache pattern as utils/payrollFeature.js — one fetch shared
 * across the app, every save broadcasts to mounted hooks so the
 * route guards flip without a reload.
 *
 * Default polarity is INVERSE to the Payroll flag: missing key reads
 * as ON because the homepage has been visible for the lifetime of the
 * project; we don't want a fresh deploy with an empty site_content
 * table to suddenly hide the school's landing page.
 */

const SITE_CONTENT_KEY = "homepage.enabled";

let cache = true;          // resolved boolean — DEFAULTS TO ON
let hydrated = false;      // true once the first /api/site-content read returned
let inflight = null;       // in-flight Promise so concurrent callers share the same fetch
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(cache);
}

/** Translate a raw site-content value to the boolean. Missing / empty
 *  / unrecognised → ON (the default). Only the literal string "false"
 *  flips it OFF, matching how the SettingsPage saves it. */
function readBool(raw) {
  if (raw == null) return true;
  return String(raw).toLowerCase() !== "false";
}

/** Fetch `/api/site-content`, read the homepage.enabled flag, update
 *  the cache and notify subscribers. Idempotent — multiple in-flight
 *  callers share the same Promise. */
export async function fetchHomepageEnabled(force = false) {
  if (!force && hydrated) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/site-content`)
    .then(r => r.ok ? r.json() : {})
    .then(map => {
      cache = readBool(map && map[SITE_CONTENT_KEY]);
      hydrated = true;
      notify();
      return cache;
    })
    .catch(() => {
      // Network error — leave cache as the last-known value so a
      // hiccup never makes the homepage suddenly disappear. hydrated
      // stays false so the next call retries.
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Optimistic local update — call after a successful PUT to
 *  /api/site-content so the route guards flip immediately without
 *  waiting for the next /api/site-content fetch. */
export function setHomepageEnabledLocally(next) {
  cache = !!next;
  hydrated = true;
  notify();
}

/** React hook. Returns the current boolean and triggers a fetch on
 *  first mount if the cache is empty. */
export function useHomepageEnabled() {
  const [enabled, setEnabled] = useState(cache);
  useEffect(() => {
    let mounted = true;
    listeners.add(setEnabled);
    if (!hydrated) {
      fetchHomepageEnabled().then(v => { if (mounted) setEnabled(v); });
    }
    return () => {
      mounted = false;
      listeners.delete(setEnabled);
    };
  }, []);
  return enabled;
}
